import { Controller } from '@hotwired/stimulus'

// Generic tab controller that persists the active tab across form submissions
// by injecting a hidden `query[tab]` input into the guardian filters form.
//
// Usage:
//   <div data-controller="guardian-context-tabs"
//        data-guardian-context-tabs-active-tab-value="<%= @active_tab %>"
//        data-guardian-context-tabs-form-id-value="my-filters-form">
//
//     <!-- Nav links: add data-tab-id and data-action -->
//     <a data-tab-id="foo" data-action="click->guardian-context-tabs#switch">Foo</a>
//     <a data-tab-id="bar" data-action="click->guardian-context-tabs#switch">Bar</a>
//
//     <!-- Pane targets: add data-guardian-context-tabs-target="pane" + data-tab-id -->
//     <div data-guardian-context-tabs-target="pane" data-tab-id="foo">...</div>
//     <div data-guardian-context-tabs-target="pane" data-tab-id="bar">...</div>
//   </div>
//
// In the controller: tab = params.dig(:query, :tab)

export default class extends Controller {
  static targets = ['pane']
  static values = {
    activeTab: String,
    formId: { type: String, default: 'guardian-filters-form' },
  }

  connect() {
    this.#currentTab = this.activeTabValue || this.#firstTabId()
    this.#activate(this.#currentTab)
    this.#syncFormInput(this.#currentTab)

    // Sync on every submit to survive race conditions after Turbo frame swaps.
    // If connect() fires before the form is in the DOM, the initial injection
    // above is a no-op; the submit listener ensures the value is always present.
    this.#boundOnSubmit = this.#onSubmit.bind(this)
    document.addEventListener('submit', this.#boundOnSubmit, true)
  }

  disconnect() {
    document.removeEventListener('submit', this.#boundOnSubmit, true)
  }

  switch(event) {
    event.preventDefault()
    this.#currentTab = event.currentTarget.dataset.tabId
    this.#activate(this.#currentTab)
    this.#syncFormInput(this.#currentTab)
    this.#reflectInUrl(this.#currentTab)
  }

  // private

  #currentTab = null
  #boundOnSubmit = null

  #onSubmit(event) {
    const form = document.getElementById(this.formIdValue)
    if (!form || event.target !== form) return
    this.#syncFormInput(this.#currentTab)
  }

  #firstTabId() {
    return this.element.querySelector('[data-tab-id]')?.dataset.tabId
  }

  #activate(tabId) {
    this.element.querySelectorAll('[data-tab-id].nav-link').forEach((link) => {
      link.classList.toggle('active', link.dataset.tabId === tabId)
    })

    this.paneTargets.forEach((pane) => {
      const active = pane.dataset.tabId === tabId
      pane.classList.toggle('active', active)
      pane.classList.toggle('show', active)
    })
  }

  #syncFormInput(tabId) {
    const form = document.getElementById(this.formIdValue)
    if (!form) return

    let input = form.querySelector('input[name="query[context_tab]"]')
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'query[context_tab]'
      form.appendChild(input)
    }
    input.value = tabId
  }

  #reflectInUrl(tabId) {
    const url = new URL(window.location.href)
    url.searchParams.set('query[context_tab]', tabId)
    window.history.replaceState(window.history.state, '', url)
  }
}
